/**
 * BEGIN CUSTOM EDIT: AJAX Product Switching
 * Date: 2025-01-24
 * What: Seamless product color switching without page reload using AJAX + Section Rendering API
 * Why: Improve UX by avoiding page reloads while maintaining separate products for SEO
 */

class ProductVariationSwitcher {
  constructor() {
    this.cache = new Map();
    this.cacheAccessOrder = []; // Track access order for LRU (Least Recently Used)
    this.cacheMaxSize = 30; // Compact full-section payloads; keep the old effective 30-product cap
    this.cacheTTL = 5 * 60 * 1000; // 5 minutes TTL to prevent stale data on long sessions
    this.isTransitioning = false;
    this.currentHandle = this.getCurrentProductHandle();
    this.abortController = null; // For cancelling in-flight requests
    this.pendingRequests = new Map();
    this.activeRequestId = 0;
    this.activeViewTransitionId = 0;
    this.activeScrollAnchorCommitId = 0;
    this.lastCommittedHistory = null;
    // BEGIN CUSTOM EDIT: setTimeout Cleanup | Date: 2025-10-19 | What: Store timeout IDs for cleanup to prevent memory leaks | Updated: 2025-01-21 - Add preloadingHandles as instance variable to prevent unbounded closure growth
    this.preloadTimeouts = new Map(); // Track preload timeouts for cleanup
    this.preloadingHandles = new Set(); // Track currently preloading handles (instance variable for proper cleanup)
    // END CUSTOM EDIT

    // Preload nearby products on hover
    this.setupHoverPreload();
    this.seedInitialHistoryState();
    document.addEventListener('variant:change', () => {
      if (this.getCurrentProductHandle() === this.currentHandle) {
        this.storeCommittedHistoryState();
      }
    });

    // BEGIN CUSTOM EDIT: Beforeunload Cleanup | Date: 2025-10-19 | What: Clean up resources when user navigates away
    window.addEventListener('beforeunload', () => this.cleanup(), { once: true });
    // END CUSTOM EDIT
  }

  /**
   * Get current product handle from URL
   */
  getCurrentProductHandle() {
    const pathParts = window.location.pathname.split('/');
    const productIndex = pathParts.indexOf('products');
    return productIndex !== -1 ? pathParts[productIndex + 1] : null;
  }

  /**
   * BEGIN CUSTOM EDIT: LRU Cache Management | Date: 2025-10-19 | What: LRU eviction + TTL expiration | Updated: 2025-11-27 - Add 5min TTL to prevent stale data
   * Manage cache size using LRU (Least Recently Used) eviction strategy
   */
  manageCacheSize() {
    if (this.cache.size > this.cacheMaxSize) {
      // Remove oldest 20% of entries when limit exceeded
      const toRemove = Math.ceil(this.cacheMaxSize * 0.2);

      for (let i = 0; i < toRemove && this.cacheAccessOrder.length > 0; i++) {
        const oldestKey = this.cacheAccessOrder.shift();
        this.cache.delete(oldestKey);
      }

      console.log(`LRU cache: Removed ${toRemove} oldest entries. Current size: ${this.cache.size}`);
    }
  }

  /**
   * Get from cache and update access order
   * Returns null if entry is expired (older than cacheTTL)
   */
  getCached(key) {
    if (!this.cache.has(key)) return null;

    const entry = this.cache.get(key);

    // Check TTL - return null if expired
    if (Date.now() - entry.timestamp > this.cacheTTL) {
      this.cache.delete(key);
      const index = this.cacheAccessOrder.indexOf(key);
      if (index > -1) this.cacheAccessOrder.splice(index, 1);
      return null;
    }

    // Move to end of access order (most recently used)
    const index = this.cacheAccessOrder.indexOf(key);
    if (index > -1) {
      this.cacheAccessOrder.splice(index, 1);
    }
    this.cacheAccessOrder.push(key);

    return entry.value;
  }

  /**
   * Set in cache with timestamp and update access order
   */
  setCached(key, value) {
    this.cache.set(key, { value, timestamp: Date.now() });

    // Add to end of access order (most recently used)
    const index = this.cacheAccessOrder.indexOf(key);
    if (index > -1) {
      this.cacheAccessOrder.splice(index, 1);
    }
    this.cacheAccessOrder.push(key);

    // Check if cache size exceeded
    this.manageCacheSize();
  }
  // END CUSTOM EDIT

  getSectionIds() {
    const mainSectionId = document.querySelector('.shopify-section--main-product')?.id?.replace('shopify-section-', '') || 'main-product';
    const announcementSectionId = document.querySelector('.shopify-section--announcement-bar')?.id?.replace('shopify-section-', '') || null;

    return { mainSectionId, announcementSectionId };
  }

  normalizeTarget(target) {
    const rawTarget = String(target || '');
    const isProductUrl = rawTarget.includes('/products/');
    const url = isProductUrl
      ? new URL(rawTarget, window.location.origin)
      : new URL(`${window.Shopify.routes.root}products/${rawTarget}`, window.location.origin);
    const pathParts = url.pathname.split('/');
    const productIndex = pathParts.indexOf('products');
    const handle = productIndex !== -1 ? pathParts[productIndex + 1] : rawTarget;

    return { handle, url, isHandleOnly: !isProductUrl };
  }

  getPayloadCacheKey(targetInfo) {
    const { mainSectionId, announcementSectionId } = this.getSectionIds();
    const targetPath = `${targetInfo.url.pathname}${targetInfo.url.search}`;
    return `payload-${targetPath}-${mainSectionId}-${announcementSectionId || 'no-announcement'}`;
  }

  parseProductData(doc) {
    const dataScript = doc.querySelector('[data-product-switch-data]');
    if (!dataScript) {
      throw new Error('Product switch metadata missing from rendered section');
    }

    const productData = JSON.parse(dataScript.textContent || '{}');
    if (!productData?.id || !productData?.handle || !Array.isArray(productData.variants)) {
      throw new Error('Product switch metadata is malformed');
    }

    productData.selected_variant = productData.selected_variant || productData.variants.find((variant) => String(variant.id) === String(productData.selected_variant_id)) || null;
    return productData;
  }

  async fetchProductPayload(targetInfo, signal = null) {
    const cacheKey = this.getPayloadCacheKey(targetInfo);
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    const pending = this.pendingRequests.get(cacheKey);
    if (pending && !pending.signal?.aborted) {
      return pending.promise;
    }
    if (pending) {
      this.pendingRequests.delete(cacheKey);
    }

    const { mainSectionId, announcementSectionId } = this.getSectionIds();
    const sectionIds = [mainSectionId, announcementSectionId].filter(Boolean);
    const url = new URL(targetInfo.url.toString());
    url.searchParams.set('sections', sectionIds.join(','));

    const request = (async () => {
      const options = signal ? { signal } : {};
      const response = await fetch(url.toString(), options);
      if (!response.ok) {
        throw new Error(`Section Rendering API request failed: ${response.status}`);
      }

      const sections = await response.json();
      if (!sections || typeof sections !== 'object') {
        throw new Error('Section Rendering API returned an invalid sections payload');
      }
      const sectionHTML = sections[mainSectionId];
      if (!sectionHTML) {
        throw new Error(`Rendered product section missing: ${mainSectionId}`);
      }
      if (announcementSectionId && sections[announcementSectionId] == null) {
        throw new Error(`Rendered announcement section missing: ${announcementSectionId}`);
      }

      const parsed = this.parseSection(sectionHTML);
      if (!parsed.info || !parsed.productForm) {
        throw new Error('Rendered product section is missing required product content');
      }

      const productData = this.parseProductData(parsed.doc);
      if (productData.handle !== targetInfo.handle) {
        throw new Error(`Rendered product handle mismatch: expected ${targetInfo.handle}, got ${productData.handle}`);
      }
      if (productData.url && !new URL(productData.url, window.location.origin).pathname.endsWith(`/products/${targetInfo.handle}`)) {
        throw new Error(`Rendered product URL mismatch: expected ${targetInfo.handle}, got ${productData.url}`);
      }
      const renderedPickerHandle = parsed.variantPicker?.getAttribute('handle');
      if (renderedPickerHandle && renderedPickerHandle !== targetInfo.handle) {
        throw new Error(`Rendered variant picker handle mismatch: expected ${targetInfo.handle}, got ${renderedPickerHandle}`);
      }
      const renderedFormId = parsed.productForm.getAttribute('id') || '';
      const renderedPickerFormId = parsed.variantPicker?.getAttribute('form-id') || '';
      if (!renderedFormId) {
        throw new Error('Rendered product form is missing its form ID');
      }
      if (parsed.variantPicker && renderedPickerFormId !== renderedFormId) {
        throw new Error('Rendered product form does not match rendered picker form binding');
      }
      if (!parsed.variantPicker && productData.has_only_default_variant !== true) {
        throw new Error('Rendered product section is missing its required variant picker');
      }
      const renderedVariantInput = parsed.productForm.querySelector('input[name="id"]');
      const renderedVariantId = renderedVariantInput?.value || null;
      if (!renderedVariantId) {
        throw new Error('Rendered product form is missing selected variant input');
      }
      if (productData.selected_variant_id != null && String(renderedVariantId) !== String(productData.selected_variant_id)) {
        throw new Error('Rendered product form selected variant does not match product metadata');
      }
      const expectedVariantId = targetInfo.url.searchParams.get('variant');
      if (expectedVariantId && String(renderedVariantId) !== String(expectedVariantId)) {
        throw new Error(`Rendered selected variant mismatch: expected ${expectedVariantId}, got ${renderedVariantId}`);
      }
      const payload = {
        productData,
        sectionHTML,
        announcementBarHTML: announcementSectionId ? sections[announcementSectionId] || null : null,
        imageDescriptor: parsed.eagerImage
      };

      this.setCached(cacheKey, payload);
      return payload;
    })();

    this.pendingRequests.set(cacheKey, { promise: request, signal });
    request.then(() => {
      if (this.pendingRequests.get(cacheKey)?.promise === request) {
        this.pendingRequests.delete(cacheKey);
      }
    }, () => {
      if (this.pendingRequests.get(cacheKey)?.promise === request) {
        this.pendingRequests.delete(cacheKey);
      }
    });

    return request;
  }


  /**
   * BEGIN CUSTOM EDIT: Parse Breadcrumbs | Date: 2025-10-18 | What: Include breadcrumbs in parsed section
   * Extract and parse section HTML
   */
  parseSection(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    return {
      doc: doc, // Store the full document for searching anywhere
      gallery: doc.querySelector('.product-gallery'),
      info: doc.querySelector('.product-info'),
      breadcrumbs: doc.querySelector('.product-breadcrumbs-desktop'),
      title: doc.querySelector('.product-info__title'),
      price: doc.querySelector('.product-info__price'),
      variantPicker: doc.querySelector('variant-picker'),
      productForm: doc.querySelector('form[action*="/cart/add"]'),
      eagerImage: this.getEagerGalleryImage(doc),
      badges: doc.querySelector('.product-info__badge-list'),
      sku: doc.querySelector('.product-info__sku'),
      inventory: doc.querySelector('.product-info__inventory')
    };
  }
  /**
   * END CUSTOM EDIT
   */

  getEagerGalleryImage(doc) {
    const image = doc.querySelector('.product-gallery__media:not([hidden]) img[fetchpriority="high"][loading="eager"]')
      || doc.querySelector('.product-gallery__media:not([hidden]) img[fetchpriority="high"]')
      || doc.querySelector('.product-gallery__media:not([hidden]) img[loading="eager"]')
      || doc.querySelector('.product-gallery__media:not([hidden]) img');

    if (!image) return null;

    return {
      src: image.getAttribute('src') || '',
      srcset: image.getAttribute('srcset') || '',
      sizes: image.getAttribute('sizes') || ''
    };
  }

  warmGalleryImage(imageDescriptor, signal, requestId) {
    if (!imageDescriptor?.src && !imageDescriptor?.srcset) return Promise.resolve();

    const image = new Image();
    if (imageDescriptor.sizes) image.sizes = imageDescriptor.sizes;
    if (imageDescriptor.srcset) image.srcset = imageDescriptor.srcset;
    if (imageDescriptor.src) image.src = imageDescriptor.src;

    const decodePromise = typeof image.decode === 'function'
      ? image.decode().catch(() => {})
      : new Promise((resolve) => {
          image.onload = resolve;
          image.onerror = resolve;
        });

    return Promise.race([
      decodePromise,
      new Promise(resolve => setTimeout(resolve, 350))
    ]).then(() => this.assertCurrentRequest(requestId, signal));
  }

  /**
   * BEGIN CUSTOM EDIT: Shopify Event System Integration | Date: 2025-10-19 | What: Replace form first, then use Shopify's product:rerender event for gallery updates
   * Update DOM with new product content using Shopify's Section Rendering API pattern
   */
  updateDOM(productData, sectionHTML, announcementBarHTML = null) {
    const parsed = this.parseSection(sectionHTML);
    // BEGIN CUSTOM EDIT: Stable viewport during product replacement | Date: 2026-09-15
    // Replaced gallery/info nodes cannot serve as stable browser scroll anchors.
    // End the opt-out after layout; an older commit must not release a newer one.
    const productSection = document.querySelector('.product-info')?.closest('.shopify-section--main-product');
    if (productSection) {
      const commitId = String(++this.activeScrollAnchorCommitId);
      productSection.setAttribute('data-product-switch-commit', commitId);
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (productSection.getAttribute('data-product-switch-commit') === commitId) {
          productSection.removeAttribute('data-product-switch-commit');
        }
      }));
    }
    // END CUSTOM EDIT
    document.querySelector('.product-info')?.closest('product-rerender')?.prepareChoiceState(parsed.info);

    // 1. Install the final form owner FIRST (form ID changes between products).
    // On the real PDP the product form lives inside the buy-buttons block; replacing
    // a standalone form first and then replacing buy-buttons later leaves picker/gallery
    // bound to a detached form clone. Themes without a buy-buttons form fall back to
    // direct form replacement.
    const currentBuyButtonsBlock = document.querySelector('.product-info [data-block-type="buy-buttons"]');
    const newBuyButtonsBlock = parsed.info.querySelector('[data-block-type="buy-buttons"]');
    if (currentBuyButtonsBlock && newBuyButtonsBlock) {
      currentBuyButtonsBlock.replaceWith(newBuyButtonsBlock.cloneNode(true));
    } else {
      const currentForm = document.querySelector('form[action*="/cart/add"]');
      const newForm = parsed.productForm;
      if (currentForm && newForm) {
        currentForm.replaceWith(newForm.cloneNode(true));
      }
    }

    // 2. Replace the gallery after the final form exists, so components bind the live form.
    const currentGallery = document.querySelector('.product-gallery');
    if (currentGallery && parsed.gallery) {
      currentGallery.replaceWith(parsed.gallery.cloneNode(true));
    }

    // 3. Update breadcrumbs
    const currentBreadcrumbs = document.querySelector('.product-breadcrumbs-desktop');
    if (currentBreadcrumbs && parsed.breadcrumbs) {
      currentBreadcrumbs.innerHTML = parsed.breadcrumbs.innerHTML;
    }

    // 4. Update all product info blocks using block-level rendering
    // This automatically handles: title, price, badges, SKU, inventory, text blocks, etc.
    const currentBlocks = document.querySelectorAll('.product-info [data-block-id]');
    currentBlocks.forEach(currentBlock => {
      const blockId = currentBlock.dataset.blockId;
      const blockType = currentBlock.dataset.blockType;
      if (!blockId) return;

      // Skip form-owning and persistent blocks updated separately to preserve live bindings/state
      if (blockType === 'buy-buttons' || blockType === 'product-variations' || blockType === 'associated-products') {
        return;
      }

      // Find matching block in new HTML
      const newBlock = parsed.info.querySelector(`[data-block-id="${blockId}"]`);
      if (newBlock) {
        if (blockType === 'variant-picker') {
          currentBlock.replaceWith(newBlock.cloneNode(true));
        } else {
          // Replace innerHTML for other blocks to preserve Shopify's rendering
          currentBlock.innerHTML = newBlock.innerHTML;
        }
      }
    });

    // 5. Update product-variations blocks (preserve open state)
    const allVariationBlocks = document.querySelectorAll('[data-block-type="product-variations"]');
    allVariationBlocks.forEach(currentBlock => {
      const blockId = currentBlock.dataset.blockId;
      if (!blockId) return;
      this.updateProductVariationsSelection(currentBlock, parsed.doc, blockId);
    });

    // 6. Update complementary products (skeleton → re-fetch instead of empty flash)
    this.updateComplementaryProducts(productData);

    // 7. Update announcement bar CSS if present (for product-specific metafield styling)
    if (announcementBarHTML) {
      this.updateAnnouncementBar(announcementBarHTML);
    }

    // BEGIN CUSTOM EDIT: Rebind product-rerender to new form | Date: 2026-04-27 | What: Update wrapper id+observe-form so option-info refreshes after AJAX product switch
    // The <product-rerender> wrapper persists across AJAX product switches but its
    // observe-form attribute still points at the old (now removed) form, so
    // selectCombination's product:rerender event lands on a dead listener.
    // Sync attributes from the new HTML and reconnect.
    this.rebindProductRerender(parsed.doc);
    document.querySelector('.product-info')?.closest('product-rerender')?.observeChoiceTitles();
    // END CUSTOM EDIT

    // 8. Notify delegated product utilities that block markup changed.
    document.dispatchEvent(new CustomEvent('product:blocks-updated'));
  }
  /**
   * END CUSTOM EDIT
   */

  /**
   * BEGIN CUSTOM EDIT: Update Product Variations Selection | Date: 2025-10-18 | What: Update only selected state without replacing entire block
   * Update product-variations block selection state without replacing DOM
   */
  updateProductVariationsSelection(currentBlock, newDocument, blockId) {
    const newBlock = newDocument.querySelector(`[data-block-id="${blockId}"]`);
    if (!newBlock) {
      console.error('Product variation block not found:', blockId);
      return;
    }
    if (!currentBlock.querySelector('.product-variations-toggle__content') || !newBlock.querySelector('.product-variations-toggle__content')) {
      currentBlock.replaceChildren(...newBlock.cloneNode(true).childNodes);
      return;
    }

    // 1. Update the inline swatch display (color name and swatch next to "Color:")
    const currentInlineDisplay = currentBlock.querySelector('.variant-picker__option-info');
    const newInlineDisplay = newBlock.querySelector('.variant-picker__option-info');

    let toggle = currentBlock.querySelector('product-variations-toggle');
    let wasExpanded = toggle?.toggleButton?.getAttribute('aria-expanded') === 'true';

    if (currentInlineDisplay && newInlineDisplay) {
      const currentButton = currentInlineDisplay.querySelector('.product-variations-toggle__button');
      const nextInlineDisplay = newInlineDisplay.cloneNode(true);
      const nextButton = nextInlineDisplay.querySelector('.product-variations-toggle__button');

      if (currentButton && nextButton) {
        currentButton.innerHTML = nextButton.innerHTML;
        currentButton.setAttribute('aria-expanded', wasExpanded ? 'true' : 'false');
        nextButton.replaceWith(currentButton);
      }

      currentInlineDisplay.replaceChildren(...Array.from(nextInlineDisplay.childNodes));

      if (toggle) {
        toggle.toggleButton = currentInlineDisplay.querySelector('.product-variations-toggle__button');
        toggle.showText = currentInlineDisplay.querySelector('.product-variations-toggle__show-text');
        toggle.hideText = currentInlineDisplay.querySelector('.product-variations-toggle__hide-text');
        toggle.content = toggle.querySelector('.product-variations-toggle__content');
        if (wasExpanded && toggle.content) {
          toggle.content.hidden = false;
          toggle.toggleButton?.setAttribute('aria-expanded', 'true');
          toggle.showText?.classList.add('hidden');
          toggle.hideText?.classList.remove('hidden');
        }
      }
    }

    // 2. Update swatch list - check if we need full replacement or selective update
    const currentSwatches = currentBlock.querySelectorAll('[data-product-handle]');
    const newSwatches = newBlock.querySelectorAll('[data-product-handle]');

    const currentHandles = Array.from(currentSwatches).map(s => s.dataset.productHandle);
    const newHandles = Array.from(newSwatches).map(s => s.dataset.productHandle);
    const hasExactHandleList = currentHandles.length === newHandles.length
      && currentHandles.every((handle, index) => handle === newHandles[index]);

    if (!hasExactHandleList) {
      // Different product group - replace the rendered swatch list so stale sibling choices disappear.
      const currentContent = currentBlock.querySelector('.product-variations-toggle__content');
      const newContent = newBlock.querySelector('.product-variations-toggle__content');
      if (currentContent && newContent) {
        currentContent.innerHTML = newContent.innerHTML;
        if (wasExpanded) {
          currentContent.hidden = false;
          toggle = currentBlock.querySelector('product-variations-toggle');
          if (toggle) {
            toggle.content = currentContent;
            toggle.toggleButton = currentBlock.querySelector('.product-variations-toggle__button');
            toggle.showText = currentBlock.querySelector('.product-variations-toggle__show-text');
            toggle.hideText = currentBlock.querySelector('.product-variations-toggle__hide-text');
            toggle.toggleButton?.setAttribute('aria-expanded', 'true');
            toggle.showText?.classList.add('hidden');
            toggle.hideText?.classList.remove('hidden');
          }
        }
      }
    } else {
      // Same products - selective update of selected state
      // Create a map of new swatch states by handle
      const newSwatchStates = new Map();
      newSwatches.forEach(newSwatch => {
        const handle = newSwatch.dataset.productHandle;
        const link = newSwatch.querySelector('a');
        const isSelected = link?.classList.contains('is-selected');
        newSwatchStates.set(handle, isSelected);
      });

      // Update current swatches to match new states
      currentSwatches.forEach(currentSwatch => {
        const handle = currentSwatch.dataset.productHandle;
        const link = currentSwatch.querySelector('a');
        if (!link) return;

        const shouldBeSelected = newSwatchStates.get(handle);
        if (shouldBeSelected) {
          link.classList.add('is-selected');
        } else {
          link.classList.remove('is-selected');
        }
      });
    }
  }
  /**
   * END CUSTOM EDIT
   */

  /**
   * BEGIN CUSTOM EDIT: Complementary Products Skeleton | Date: 2026-03-24 | What: Show skeleton while re-fetching complementary products on variant switch
   * Update complementary products with skeleton placeholder instead of empty flash
   */
  updateComplementaryProducts(productData) {
    const recEl = document.querySelector('product-recommendations[intent="complementary"]');
    if (!recEl) return;

    // Show 2 skeleton cards (matches the 2-up carousel view)
    const skeletonCount = 2;

    // Build skeleton via safe DOM methods
    const wrapper = document.createElement('div');
    wrapper.className = 'product-info__complementary-products v-stack gap-3 sm:gap-4';

    // Preserve title row if present
    const existingTitle = recEl.querySelector('.h-stack.justify-between');
    if (existingTitle) {
      wrapper.appendChild(existingTitle.cloneNode(true));
    }

    // Simple grid skeleton — no carousel complexity needed for loading state
    const grid = document.createElement('div');
    grid.className = 'complementary-skeleton';

    for (let i = 0; i < skeletonCount; i++) {
      const card = document.createElement('div');
      card.className = 'complementary-skeleton__card';

      const imgSkel = document.createElement('div');
      imgSkel.className = 'complementary-skeleton__img skeleton';

      const textWrap = document.createElement('div');
      textWrap.className = 'complementary-skeleton__text';
      const line1 = document.createElement('div');
      line1.className = 'skeleton skeleton--text';
      line1.style.width = `${70 - i * 10}%`;
      const line2 = document.createElement('div');
      line2.className = 'skeleton skeleton--text';
      line2.style.width = `${45 + i * 5}%`;
      const line3 = document.createElement('div');
      line3.className = 'skeleton skeleton--text';
      line3.style.width = '30%';
      textWrap.appendChild(line1);
      textWrap.appendChild(line2);
      textWrap.appendChild(line3);

      card.appendChild(imgSkel);
      card.appendChild(textWrap);
      grid.appendChild(card);
    }

    wrapper.appendChild(grid);
    const nextRecommendations = recEl.cloneNode(false);
    nextRecommendations.setAttribute('product', productData.id);
    nextRecommendations.replaceChildren(wrapper);
    recEl.replaceWith(nextRecommendations);
  }
  /**
   * END CUSTOM EDIT
   */

  /**
   * Update announcement bar CSS for product-specific metafield styling
   */
  updateAnnouncementBar(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newStyle = doc.querySelector('style');
    const currentStyle = document.querySelector('.shopify-section--announcement-bar style');

    if (newStyle && currentStyle) {
      currentStyle.innerHTML = newStyle.innerHTML;
    }
  }

  /**
   * BEGIN CUSTOM EDIT: Rebind product-rerender wrapper | Date: 2026-04-27
   * What: After AJAX product switch, the <product-rerender> wrapper element
   * persists with stale id and observe-form attributes referencing the previous
   * product's form. Sync attributes from the new HTML; ProductRerender's
   * native attributeChangedCallback binds to the new observe-form target.
   */
  rebindProductRerender(newDocument) {
    // Pair each existing <product-rerender> with its counterpart in the new HTML
    // by matching the section-id suffix (template--XYZ__main), which is stable
    // across products. The product-id portion differs per product.
    const sectionSuffixOf = (id) => {
      if (!id) return null;
      const match = id.match(/template--[a-z0-9_]+/i);
      return match ? match[0] : null;
    };
    const roleOf = (id) => {
      if (!id) return 'main';
      if (id.endsWith('-sticky-bar')) return 'sticky';
      return 'main';
    };

    document.querySelectorAll('product-rerender').forEach(currentRerender => {
      const currentId = currentRerender.getAttribute('id');
      const currentObserve = currentRerender.getAttribute('observe-form');
      const sectionSuffix = sectionSuffixOf(currentId) || sectionSuffixOf(currentObserve);
      const role = roleOf(currentId);
      if (!sectionSuffix) return;

      const newCandidates = Array.from(newDocument.querySelectorAll('product-rerender'))
        .filter(el => sectionSuffixOf(el.getAttribute('id')) === sectionSuffix)
        .filter(el => roleOf(el.getAttribute('id')) === role);
      const newRerender = newCandidates[0];
      if (!newRerender) return;

      const newId = newRerender.getAttribute('id');
      const newObserve = newRerender.getAttribute('observe-form');
      if (newId === currentId && newObserve === currentObserve) return;

      if (newId) currentRerender.setAttribute('id', newId);
      if (newObserve) currentRerender.setAttribute('observe-form', newObserve);
    });
  }
  /**
   * END CUSTOM EDIT
   */

  /**
   * BEGIN CUSTOM EDIT: Update document title | Date: 2025-10-20 | What: Update browser tab title after AJAX product switch
   * Update browser URL and document title without reload
   */
  updateURL(productData, targetInfo, updateHistory = true) {
    const newURL = `${targetInfo.url.pathname}${targetInfo.url.search}`;

    // Update document title to match new product
    if (productData && productData.title) {
      // Extract store name from current title (everything after last '|')
      const currentTitle = document.title;
      const storeName = currentTitle.includes('|')
        ? currentTitle.substring(currentTitle.lastIndexOf('|')).trim()
        : '| Senkels';
      document.title = `${productData.title} ${storeName}`;
    }

    if (updateHistory) {
      window.history.pushState(
        { ...(window.history.state || {}), productHandle: targetInfo.handle, ajaxSwitch: true, path: newURL },
        '',
        newURL
      );
    }
  }
  /**
   * END CUSTOM EDIT
   */

  seedInitialHistoryState() {
    if (!this.currentHandle) return;

    if (!window.history.state?.productHandle) {
      window.history.replaceState(
        { ...(window.history.state || {}), productHandle: this.currentHandle, ajaxSwitch: true, path: window.location.href },
        '',
        window.location.href
      );
    }

    this.storeCommittedHistoryState();
  }

  storeCommittedHistoryState() {
    this.lastCommittedHistory = {
      state: { ...(window.history.state || {}), path: window.location.href },
      url: window.location.href,
      title: document.title
    };
  }

  restoreCommittedHistoryState() {
    if (!this.lastCommittedHistory) return;

    window.history.replaceState(
      this.lastCommittedHistory.state,
      '',
      this.lastCommittedHistory.url
    );
    document.title = this.lastCommittedHistory.title;
  }

  // BEGIN CUSTOM EDIT: Product Switch Navigation Fallback | Date: 2026-09-15 | What: Restore native navigation when a clicked product swatch cannot render through Section Rendering API
  navigateToProductFallback(targetInfo) {
    const fallbackURL = new URL(targetInfo.url.toString(), window.location.origin);
    window.location.assign(fallbackURL.toString());
  }
  // END CUSTOM EDIT

  assertCurrentRequest(requestId, signal) {
    if (signal?.aborted || requestId !== this.activeRequestId) {
      throw new DOMException('Product switch request superseded', 'AbortError');
    }
  }

  cancelNativePendingSelection() {
    document.querySelectorAll('variant-picker').forEach((picker) => {
      if (typeof picker.cancelPendingSelection === 'function') {
        picker.cancelPendingSelection({ restore: true });
      }
    });
  }

  commitProductState(productData, targetInfo, updateHistory) {
    // Update URL and document title
    this.updateURL(productData, targetInfo, updateHistory);

    // Update current handle and product context globals
    this.currentHandle = targetInfo.handle;
    window.__senkelsProductType = productData.type || '';

    // BEGIN CUSTOM EDIT: Sync ShopifyAnalytics.meta | Date: 2026-02-02 | What: Update ShopifyAnalytics.meta.product BEFORE analytics.publish to fix stale shopify_ZZ tracking IDs
    if (window.ShopifyAnalytics?.meta?.product) {
      const selectedVariant = productData.selected_variant || productData.variants?.find(v => v.available) || productData.variants?.[0];
      window.ShopifyAnalytics.meta.product = {
        id: productData.id,
        gid: productData.gid || `gid://shopify/Product/${productData.id}`,
        vendor: productData.vendor || '',
        type: productData.type || '',
        handle: productData.handle,
        variants: productData.variants?.map(v => ({
          id: v.id,
          price: v.price,
          name: `${productData.title} - ${v.title}`,
          public_title: v.title,
          sku: v.sku || ''
        })) || []
      };
      // Update selected variant if meta.selectedVariant exists
      if (window.ShopifyAnalytics.meta.selectedVariant !== undefined) {
        window.ShopifyAnalytics.meta.selectedVariant = selectedVariant ? {
          id: selectedVariant.id,
          price: selectedVariant.price,
          name: `${productData.title} - ${selectedVariant.title}`,
          public_title: selectedVariant.title,
          sku: selectedVariant.sku || ''
        } : null;
      }
    }
    // END CUSTOM EDIT

    // Track analytics (fires AFTER meta is updated so Web Pixels get correct product data)
    if (window.Shopify && window.Shopify.analytics) {
      window.Shopify.analytics.publish('product_viewed', {
        productId: productData.id,
        productHandle: productData.handle
      });
    }

    this.storeCommittedHistoryState();

    document.dispatchEvent(new CustomEvent('product:switched', {
      detail: { product: productData, handle: productData.handle }
    }));
  }

  /**
   * BEGIN CUSTOM EDIT: Instant Product Switching | Date: 2025-10-18 | What: Instant switching with no transitions for preloaded content | Updated: 2025-10-19 - Added pulsing animation for non-preloaded products (mobile) that runs in parallel with fetch
   * Main switch product function - instant when preloaded, pulsing animation when not
   */
  // BEGIN CUSTOM EDIT: Swatch Loading Pulse | Date: 2026-03-24 | What: Pulse only the clicked swatch instead of entire gallery/info
  async switchProduct(target, updateHistory = true, clickedSwatch = null, force = false) {
    const targetInfo = this.normalizeTarget(target);
    const targetPath = `${targetInfo.url.pathname}${targetInfo.url.search}`;
    const currentPath = `${window.location.pathname}${window.location.search}`;

    const isSameHandleOnlyTarget = targetInfo.isHandleOnly && targetInfo.handle === this.currentHandle;

    if (!force && targetInfo.handle === this.currentHandle && (targetPath === currentPath || isSameHandleOnlyTarget) && !this.isTransitioning) {
      return;
    }

    const requestId = ++this.activeRequestId;
    this.isTransitioning = true;
    this.cancelNativePendingSelection();

    // Cancel previous requests when user switches products rapidly
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();
    const activeController = this.abortController;

    if (!force && targetInfo.handle === this.currentHandle && (targetPath === currentPath || isSameHandleOnlyTarget)) {
      this.isTransitioning = false;
      this.abortController = null;
      if (clickedSwatch) clickedSwatch.classList.remove('product-switching--loading');
      return;
    }

    // Check if content is already preloaded (cached)
    const isPreloaded = this.getCached(this.getPayloadCacheKey(targetInfo)) !== null;

    try {
      // If not preloaded, pulse only the clicked swatch (not the entire page)
      if (!isPreloaded && clickedSwatch) {
        clickedSwatch.classList.add('product-switching--loading');
      }
  // END CUSTOM EDIT

      const signal = this.abortController.signal;
      const { productData, sectionHTML, announcementBarHTML, imageDescriptor } = await this.fetchProductPayload(targetInfo, signal);
      this.assertCurrentRequest(requestId, signal);
      if (clickedSwatch) {
        await this.warmGalleryImage(imageDescriptor, signal, requestId);
      }

      // BEGIN CUSTOM EDIT: View Transition for Variant Switch | Date: 2026-03-24 | What: Smooth crossfade via View Transitions API when switching variants
      const canAnimate = typeof document.startViewTransition === 'function'
        && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let hasCommitted = false;
      const commit = () => {
        if (hasCommitted) return;
        this.assertCurrentRequest(requestId, signal);
        this.commitProductState(productData, targetInfo, updateHistory);
        hasCommitted = true;
      };

      // Remove swatch pulse before DOM update
      if (!isPreloaded && clickedSwatch) {
        clickedSwatch.classList.remove('product-switching--loading');
      }

      if (canAnimate) {
        // Scope view-transition-names to product switch (avoid conflicts with theme toggle)
        const viewTransitionId = ++this.activeViewTransitionId;
        document.documentElement.classList.add('product-switching--vt');

        const transition = document.startViewTransition(() => {
          this.assertCurrentRequest(requestId, signal);
          this.updateDOM(productData, sectionHTML, announcementBarHTML);
          commit();
        });
        // A newer switch may skip the animation. DOM-update failures are still
        // handled through finished below; ready only describes animation setup.
        transition.ready.catch(() => {});

        await transition.finished.finally(() => {
          if (this.activeViewTransitionId === viewTransitionId) {
            document.documentElement.classList.remove('product-switching--vt');
          }
        });
      } else {
        // No View Transitions API or reduced motion — instant update
        this.assertCurrentRequest(requestId, signal);
        this.updateDOM(productData, sectionHTML, announcementBarHTML);
        commit();
      }
      // END CUSTOM EDIT
      this.assertCurrentRequest(requestId, signal);
      if (!hasCommitted) commit();

    } catch (error) {
      if (requestId !== this.activeRequestId || activeController.signal.aborted) {
        if (clickedSwatch) clickedSwatch.classList.remove('product-switching--loading');
        return;
      }
      // Ignore AbortErrors (these happen when user quickly switches between products)
      if (error.name === 'AbortError') {
        console.log('Product switch request cancelled');
        if (clickedSwatch) clickedSwatch.classList.remove('product-switching--loading');
        return;
      }

      console.error('Error switching product:', error);
      if (!updateHistory && force) {
        this.restoreCommittedHistoryState();
      } else if (clickedSwatch && updateHistory) {
        this.navigateToProductFallback(targetInfo);
      }
      // Remove loading class if there was an error
      if (clickedSwatch) clickedSwatch.classList.remove('product-switching--loading');
    } finally {
      if (this.activeRequestId === requestId) {
        this.isTransitioning = false;
        if (this.abortController === activeController) {
          this.abortController = null;
        }
      }
    }
  }
  /**
   * END CUSTOM EDIT
   */

  /**
   * BEGIN CUSTOM EDIT: Instant Hover Preload | Date: 2025-10-18 | What: Instant preloading on mouseover for fastest response | Updated: 2025-11-27 - Uses mouseover (bubbles) instead of mouseenter (doesn't bubble)
   * Setup hover preloading for instant switching
   * NOTE: Listener intentionally NOT removable - persists for fast navigation performance
   */
  setupHoverPreload() {
    document.addEventListener('mouseover', (e) => {
      // Ensure target is an element (not a text node) before using closest
      if (!e.target || !e.target.closest) return;

      const swatch = e.target.closest('[data-ajax-switch="true"]');
      if (!swatch) return;

      const handle = swatch.dataset.productHandle;
      if (!handle || handle === this.currentHandle) return;
      const targetInfo = this.normalizeTarget(handle);

      // Skip if already preloading or cached
      if (this.preloadingHandles.has(handle)) return;
      if (this.getCached(this.getPayloadCacheKey(targetInfo)) !== null) return;

      // Mark as preloading and start immediately (no debounce)
      this.preloadingHandles.add(handle);

      this.fetchProductPayload(targetInfo).catch(() => {}).finally(() => {
        // Remove from preloading set immediately after completion
        // Cache check above will prevent unnecessary re-preloading
        this.preloadingHandles.delete(handle);
      });
    }, true); // Capture phase catches event early during propagation
  }
  /**
   * END CUSTOM EDIT
   */

  /**
   * BEGIN CUSTOM EDIT: Cleanup Method | Date: 2025-10-19 | What: Clear timeouts and abort requests | Updated: 2025-11-27
   * Cleanup method to clear pending timeouts and abort in-flight requests
   * NOTE: Document/window listeners (mouseenter, popstate) intentionally NOT removed for fast switching
   */
  cleanup() {
    // Clear all preload timeouts (no longer used but kept for backwards compatibility)
    if (this.preloadTimeouts) {
      this.preloadTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
      this.preloadTimeouts.clear();
    }

    // Clear preloading handles set
    if (this.preloadingHandles) {
      this.preloadingHandles.clear();
    }

    // Abort any in-flight requests
    if (this.abortController) {
      this.abortController.abort();
    }
  }
  /**
   * END CUSTOM EDIT
   */

  /**
   * Handle browser back/forward buttons
   * NOTE: Listener intentionally NOT removable - persists for fast navigation performance
   */
  setupHistoryNavigation() {
    window.addEventListener('popstate', (event) => {
      if (event.state?.ajaxSwitch && event.state?.productHandle) {
        this.switchProduct(event.state.path || window.location.href, false, null, true);
      }
    });
  }
}

// Initialize the switcher
if (typeof window !== 'undefined' && window.Shopify) {
  window.productVariationSwitcher = new ProductVariationSwitcher();
  window.productVariationSwitcher.setupHistoryNavigation();
}

/**
 * END CUSTOM EDIT
 */
